/**
 * Auth API Slice
 * Injects authentication endpoints into the base API slice
 * Handles login, logout, token verification, and refresh
 */

import { apiSlice } from "../api/apiSlice";
import { LoginCredentials } from "../../types";
import {
  setCredentials,
  logout as logoutAction,
} from "../../store/slices/authSlice";

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Login mutation
     * Sends credentials to backend and receives tokens as HttpOnly cookies
     * User info is returned in response body
     * Backend extracts venueId from JWT for all subsequent API calls
     */
    login: builder.mutation<
      {
        message: string;
        user: { username: string; displayName: string; role: string };
      },
      LoginCredentials
    >({
      query: (credentials) => ({
        url: "/api/login",
        method: "POST",
        body: credentials,
      }),
      invalidatesTags: ["Auth"],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          // Tokens are in HttpOnly cookies set by backend
          // User info is in response body
          const { user } = data;

          // Save user to localStorage for page refresh
          localStorage.setItem("user", JSON.stringify(user));

          // Update Redux UI state
          dispatch(
            setCredentials({
              displayName: user.displayName,
              username: user.username,
              role: user.role,
              isAuthenticated: true,
            })
          );

          console.log("Login successful:", user.displayName);
        } catch (error) {
          console.error("Login failed:", error);
          // Error is automatically handled by RTK Query
        }
      },
    }),

    /**
     * Logout mutation
     * Calls backend to clear HttpOnly cookies and clears localStorage
     */
    logout: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: "/api/logout",
        method: "POST",
      }),
      invalidatesTags: ["Auth"],
      async onQueryStarted(_, { dispatch }) {
        // Clear user from localStorage
        localStorage.removeItem("user");

        // Update Redux UI state after logout
        dispatch(logoutAction());
        console.log("Logout successful");
      },
    }),
  }),
});

// Export hooks for use in components
export const { useLoginMutation, useLogoutMutation } = authApiSlice;
